import React, { useState } from 'react';
import CalculatorCard from './CalculatorCard';
import DataStrategyCalculator from './DataStrategyCalculator';
import ComplianceCalculator from './ComplianceCalculator';
import AnalyticsReadinessChecker from './AnalyticsReadinessChecker';

const CalculatorSuite = () => {
  const [activeCalculator, setActiveCalculator] = useState(null);

  const calculators = [
    {
      id: 'data-strategy',
      title: 'Data Strategy ROI Calculator',
      description: 'Quantify the financial impact of improved data capabilities including efficiency gains, decision-making improvements, and risk reduction.',
      features: [
        'Revenue impact analysis',
        'Cost optimization projections',
        'Decision speed improvements',
        'Risk mitigation benefits'
      ],
      icon: 'TrendingUp',
      color: 'bg-secondary',
      estimatedTime: '8-10 min',
      component: DataStrategyCalculator
    },
    {
      id: 'compliance',
      title: 'Compliance Cost Calculator',
      description: 'Estimate GDPR, AI Act, and other regulatory compliance investments versus potential penalties and risk exposure.',
      features: [
        'Multi-regulation analysis',
        'Penalty risk assessment',
        'Implementation cost estimates',
        'Geographic compliance scope'
      ],
      icon: 'Shield',
      color: 'bg-error',
      estimatedTime: '6-8 min',
      component: ComplianceCalculator
    },
    {
      id: 'analytics-readiness',
      title: 'Analytics Readiness Checker',
      description: 'Evaluate current capabilities against industry benchmarks with gap analysis and improvement cost estimates.',
      features: [
        'Capability gap analysis',
        'Benchmark comparisons',
        'Improvement roadmap',
        'Investment prioritization'
      ],
      icon: 'BarChart3',
      color: 'bg-accent',
      estimatedTime: '5-7 min',
      component: AnalyticsReadinessChecker
    }
  ];

  const handleCalculatorStart = (calculatorId) => {
    setActiveCalculator(calculatorId);
  };

  const handleCalculatorClose = () => {
    setActiveCalculator(null);
  };

  const handleCalculatorComplete = (results) => {
    console.log('Calculator completed with results:', results);
    // Here you would typically:
    // 1. Send results to CRM/lead management system
    // 2. Generate PDF report
    // 3. Trigger follow-up email sequence
    // 4. Update lead scoring
    setActiveCalculator(null);
  };

  const renderActiveCalculator = () => {
    const calculator = calculators?.find(calc => calc?.id === activeCalculator);
    if (!calculator) return null;

    const CalculatorComponent = calculator?.component;
    return (
      <CalculatorComponent
        onClose={handleCalculatorClose}
        onComplete={handleCalculatorComplete}
      />
    );
  };

  return (
    <section className="py-20 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-secondary/10 text-secondary rounded-full px-4 py-2 mb-6">
            <span className="text-sm font-medium">Interactive Tools</span>
          </div>
          <h2 className="text-section-title text-primary mb-6">
            Choose Your Calculator
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Each calculator uses sophisticated logic based on industry data and our client experience 
            to provide credible projections that support your business case development.
          </p>
        </div>

        {/* Calculator Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {calculators?.map((calculator) => (
            <CalculatorCard
              key={calculator?.id}
              title={calculator?.title}
              description={calculator?.description}
              features={calculator?.features}
              icon={calculator?.icon}
              color={calculator?.color}
              estimatedTime={calculator?.estimatedTime}
              onStart={() => handleCalculatorStart(calculator?.id)}
              isActive={activeCalculator === calculator?.id}
            />
          ))}
        </div>

        {/* Value Proposition */}
        <div className="bg-gradient-to-r from-surface to-white rounded-2xl p-8 lg:p-12 border border-border">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-semibold text-primary mb-4">
                Why Use Our Calculators?
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-secondary/20 rounded-full flex items-center justify-center mt-0.5">
                    <div className="w-2 h-2 bg-secondary rounded-full"></div>
                  </div>
                  <div>
                    <div className="font-medium text-foreground">Industry-Validated Models</div>
                    <div className="text-sm text-muted-foreground">Based on 500+ client engagements and industry benchmarks</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-secondary/20 rounded-full flex items-center justify-center mt-0.5">
                    <div className="w-2 h-2 bg-secondary rounded-full"></div>
                  </div>
                  <div>
                    <div className="font-medium text-foreground">Personalized Results</div>
                    <div className="text-sm text-muted-foreground">Tailored to your industry, size, and specific requirements</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-secondary/20 rounded-full flex items-center justify-center mt-0.5">
                    <div className="w-2 h-2 bg-secondary rounded-full"></div>
                  </div>
                  <div>
                    <div className="font-medium text-foreground">Actionable Insights</div>
                    <div className="text-sm text-muted-foreground">Detailed reports with implementation roadmaps and next steps</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-secondary/20 rounded-full flex items-center justify-center mt-0.5">
                    <div className="w-2 h-2 bg-secondary rounded-full"></div>
                  </div>
                  <div>
                    <div className="font-medium text-foreground">Expert Follow-up</div>
                    <div className="text-sm text-muted-foreground">Optional consultation to validate assumptions and refine projections</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="text-center lg:text-right">
              <div className="inline-block bg-white rounded-xl p-8 shadow-subtle border border-border">
                <div className="text-4xl font-bold text-secondary mb-2">95%</div>
                <div className="text-sm text-muted-foreground mb-4">Accuracy Rate</div>
                <div className="text-xs text-muted-foreground">
                  Based on post-implementation validation with 200+ clients
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Render Active Calculator Modal */}
      {activeCalculator && renderActiveCalculator()}
    </section>
  );
};

export default CalculatorSuite;